<template>
  <main class="keep-page" :class="appStore.theme">
    <header class="hud">
      <div><small>{{ t('keep_it_up.score') }}</small><strong>{{ score }}</strong></div>
      <h1>{{ localized(game.name) || 'Keep It Up!' }}</h1>
      <div><small>{{ t('keep_it_up.rescues') }}</small><strong>{{ rescuesLeft }}</strong></div>
    </header>

    <section class="players" :style="{ '--columns': Math.min(3, Math.max(2, players.length)) }">
      <article v-for="player in players" :key="player.id" class="player" :style="{ '--colour': player.playerColour || '#ffd65a' }">
        <span class="avatar">{{ initials(playerName(player)) }}</span>
        <span class="identity"><b>{{ playerName(player) }}</b><small>{{ t('keep_it_up.boosts', { count: boosts[player.id] || 0 }) }}</small></span>
        <span class="cooldown"><i :style="{ transform: `scaleX(${cooldownProgress(player.id)})` }"></i></span>
        <i class="connection" :class="connectionClass(player)"></i>
      </article>
    </section>

    <section class="arena">
      <div class="distance">{{ t('keep_it_up.distance', { distance: distance }) }}</div>
      <div class="flight-guide"><b>{{ t('keep_it_up.when_to_tap') }}</b><span>{{ t('keep_it_up.tap_cooldown') }}</span></div>
      <div v-for="obstacle in obstacles" :key="obstacle.id" class="obstacle" :style="{ left: `${obstacle.x}%`, width: `${obstacle.width}%` }">
        <i class="upper" :style="{ height: `${obstacle.gapTop}%` }"></i>
        <i class="lower" :style="{ top: `${obstacle.gapTop + obstacle.gapSize}%` }"></i>
      </div>
      <div class="flyer" :style="{ left: `${ballX}%`, top: `${ballY}%`, transform: `translate(-50%, -50%) rotate(${rotation}deg)` }">
        <i></i><b>S</b>
      </div>
      <div class="ground"></div>
    </section>

    <section v-if="phase === 'countdown'" class="overlay"><div class="overlay-card countdown"><p>{{ t('keep_it_up.get_ready') }}</p><strong>{{ countdown }}</strong></div></section>
    <section v-else-if="phase === 'rescue'" class="overlay rescue-overlay">
      <div class="overlay-card"><h2>{{ t('keep_it_up.save_it') }}</h2><p>{{ t('keep_it_up.tap_together') }}</p><div class="rescue-meter"><i :style="{ width: `${Math.min(100, rescueProgress / rescueTarget * 100)}%` }"></i></div><strong>{{ rescueProgress }} / {{ rescueTarget }}</strong><small>{{ rescueSeconds }}s</small></div>
    </section>
    <section v-else-if="phase === 'finished'" class="overlay">
      <div class="overlay-card result"><h2>{{ t('keep_it_up.finished') }}</h2><strong>{{ t('keep_it_up.final_distance', { distance }) }}</strong><p>{{ t('keep_it_up.team_boosts', { count: totalBoosts }) }}</p></div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/appStore'
import { useWebsocketStore } from '@/stores/websocketStore'
import eventBus from '@/plugins/eventBus'
import { soundManager, SOUNDS } from '@/services/soundManager'

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t, locale } = useI18n()
const phase = ref('countdown')
const countdown = ref(3)
const ballX = 25
const ballY = ref(48)
const velocity = ref(0)
const rotation = ref(0)
const score = ref(0)
const distance = ref(0)
const obstacles = ref([])
const boosts = reactive({})
const cooldownUntil = reactive({})
const acceptedRequestIds = new Set()
const now = ref(performance.now())
const rescuesLeft = ref(2)
const rescueProgress = ref(0)
const rescueTarget = ref(6)
const rescueSeconds = ref(5)
let nextObstacleId = 1
let startedAt = 0
let lastFrame = 0
let frameId = 0
let timerId = 0
let rescueTimer = 0
let finishedSent = false

const game = computed(() => appStore.room?.game || appStore.game || {})
const players = computed(() => game.value.players || [])
const totalBoosts = computed(() => Object.values(boosts).reduce((sum, value) => sum + value, 0))

function localized(value) { return typeof value === 'object' ? value[locale.value] || value.en || Object.values(value)[0] : value }
function playerName(player) { return player?.displayName || player?.name || player?.email || t('keep_it_up.player') }
function initials(name) { return String(name).trim().split(/\s+/).map(word => word[0]).join('').slice(0, 2).toUpperCase() }
function connectionClass(player) { return player.connected === false ? 'offline' : player.inGame === true ? 'online' : 'away' }
function cooldownProgress(id) { return Math.max(0, Math.min(1, (Number(cooldownUntil[id] || 0) - now.value) / 850)) }

function resetGame() {
  soundManager.stopAll(); soundManager.play(SOUNDS.roundStart)
  cancelAnimationFrame(frameId)
  clearInterval(timerId)
  clearInterval(rescueTimer)
  phase.value = 'countdown'; countdown.value = 3; ballY.value = 48; velocity.value = 0; rotation.value = 0
  score.value = 0; distance.value = 0; obstacles.value = []; rescuesLeft.value = 2; finishedSent = false; startedAt = 0
  rescueProgress.value = 0
  Object.keys(boosts).forEach(key => delete boosts[key])
  Object.keys(cooldownUntil).forEach(key => delete cooldownUntil[key])
  players.value.forEach(player => { boosts[player.id] = 0; cooldownUntil[player.id] = 0 })
  timerId = window.setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) { clearInterval(timerId); startRun() }
  }, 1000)
}

function startRun() {
  phase.value = 'playing'
  startedAt ||= performance.now()
  lastFrame = performance.now()
  obstacles.value.push(createObstacle(105))
  frameId = requestAnimationFrame(step)
}
function createObstacle(x) {
  const difficulty = Math.min(10, score.value / 100)
  const gapSize = Math.max(27, 36 - difficulty * .7)
  const gapTop = 13 + Math.random() * (70 - gapSize)
  return { id: nextObstacleId++, x, width: 5.2, gapTop, gapSize, passed: false }
}
function step(time) {
  if (phase.value !== 'playing') return
  const dt = Math.min(.035, Math.max(0, (time - lastFrame) / 1000)); lastFrame = time; now.value = time
  const elapsed = Math.max(0, (time - startedAt) / 1000)
  const speed = 12.5 + Math.min(8, elapsed * .12)
  velocity.value += (39 + Math.min(8, elapsed * .08)) * dt
  ballY.value += velocity.value * dt
  rotation.value = Math.max(-25, Math.min(60, velocity.value * 2.2))
  obstacles.value.forEach(obstacle => {
    obstacle.x -= speed * dt
    if (!obstacle.passed && obstacle.x + obstacle.width < ballX) { obstacle.passed = true; score.value += 25 }
  })
  obstacles.value = obstacles.value.filter(obstacle => obstacle.x > -8)
  if (!obstacles.value.length || obstacles.value[obstacles.value.length - 1].x < 70) obstacles.value.push(createObstacle(105))
  distance.value = Math.floor(elapsed * 8)
  score.value = Math.max(score.value, distance.value)
  if (ballY.value < 5) { ballY.value = 5; velocity.value = 5 }
  if (ballY.value > 91 || hasCollision()) { beginRescue(); return }
  frameId = requestAnimationFrame(step)
}
function hasCollision() {
  const radiusX = 2.1; const radiusY = 3.6
  return obstacles.value.some(obstacle => ballX + radiusX > obstacle.x && ballX - radiusX < obstacle.x + obstacle.width && (ballY.value - radiusY < obstacle.gapTop || ballY.value + radiusY > obstacle.gapTop + obstacle.gapSize))
}
function handleControl(control) {
  if (control?.requestId && acceptedRequestIds.has(control.requestId)) return acknowledge(control, true, 'duplicate')
  if (control?.type !== 'action' || control?.value !== 'tap') return acknowledge(control, false, 'invalid-control')
  const player = players.value.find(candidate => candidate.id === control.userId)
  if (!player || player.connected === false || player.inGame === false) return acknowledge(control, false, 'inactive-player')
  const time = performance.now(); now.value = time
  if (time < Number(cooldownUntil[player.id] || 0)) return acknowledge(control, false, 'cooldown')
  if (phase.value === 'rescue') { if (control.requestId) acceptedRequestIds.add(control.requestId); acknowledge(control, true, 'rescue-tap'); cooldownUntil[player.id] = time + 850; rescueProgress.value += 1; boosts[player.id] = (boosts[player.id] || 0) + 1; if (rescueProgress.value >= rescueTarget.value) resumeFromRescue(); return }
  if (phase.value !== 'playing') return acknowledge(control, false, 'not-playing')
  if (control.requestId) acceptedRequestIds.add(control.requestId)
  acknowledge(control, true, 'boost-applied')
  cooldownUntil[player.id] = time + 850
  boosts[player.id] = (boosts[player.id] || 0) + 1
  soundManager.play(SOUNDS.keepItUpBoost)
  velocity.value = Math.max(-24, velocity.value - 18)
}
function acknowledge(control, accepted, reason) { if (control?.requestId && control?.userId) socketStore.send({ event: 'control-result', data: { requestId: control.requestId, userId: control.userId, accepted, reason } }) }
function beginRescue() {
  soundManager.play(SOUNDS.wrong)
  cancelAnimationFrame(frameId)
  if (rescuesLeft.value <= 0) { finishGame(); return }
  phase.value = 'rescue'; rescueProgress.value = 0; rescueSeconds.value = 5
  rescueTarget.value = Math.max(6, players.value.filter(player => player.connected !== false && player.inGame !== false).length * 3)
  rescueTimer = window.setInterval(() => { rescueSeconds.value -= 1; if (rescueSeconds.value <= 0) { clearInterval(rescueTimer); finishGame() } }, 1000)
}
function resumeFromRescue() {
  if (phase.value !== 'rescue') return
  clearInterval(rescueTimer); rescuesLeft.value -= 1; ballY.value = 48; velocity.value = -8
  soundManager.play(SOUNDS.keepItUpRescue); soundManager.schedule(SOUNDS.points, 650)
  obstacles.value = obstacles.value.filter(obstacle => obstacle.x > 48)
  lastFrame = performance.now(); phase.value = 'playing'; frameId = requestAnimationFrame(step)
}
function finishGame() {
  clearInterval(rescueTimer); soundManager.stopAll(); soundManager.play(SOUNDS.gameOver); phase.value = 'finished'
  if (finishedSent) return
  finishedSent = true
  socketStore.send({ event: 'finished', data: { gameId: appStore.game?.id, result: { score: score.value, distance: distance.value, boosts: { ...boosts } } } })
}

onMounted(() => { eventBus.on('game-control', handleControl); eventBus.on('restart-round', resetGame); socketStore.send({ event: 'playing', data: { gameId: game.value.id } }); resetGame() })
onBeforeUnmount(() => { cancelAnimationFrame(frameId); clearInterval(timerId); clearInterval(rescueTimer); soundManager.stopAll(); eventBus.off('game-control', handleControl); eventBus.off('restart-round', resetGame) })
</script>

<style scoped>
.keep-page{height:100vh;box-sizing:border-box;overflow:hidden;padding:1.5vh 2vw;background:linear-gradient(#102746 0 70%,#172334);color:#fff;font-family:'Noto Sans',sans-serif}.keep-page.light{background:linear-gradient(#dff4ff 0 70%,#e8edf2);color:#14243a}.hud{height:9vh;display:grid;grid-template-columns:1fr 3fr 1fr;align-items:center}.hud h1{margin:0;font-size:clamp(1.45rem,4vh,2.7rem)}.hud div{display:grid}.hud small{text-transform:uppercase;letter-spacing:.08em;opacity:.75;font-size:clamp(.6rem,1.5vh,.85rem)}.hud strong{font-size:clamp(1.25rem,3.4vh,2.2rem);color:#ffd65a}.players{height:15vh;display:grid;grid-template-columns:repeat(var(--columns),minmax(0,1fr));gap:.7vh 1vw;margin-bottom:1vh}.player{position:relative;min-width:0;display:grid;grid-template-columns:auto minmax(0,1fr);grid-template-rows:1fr .25rem;align-items:center;gap:.25vh .7vw;padding:.45vh .65vw;border:2px solid var(--colour);border-radius:12px;background:#081424b8}.light .player{background:#ffffffd9}.avatar{grid-row:1;width:clamp(1.7rem,4.8vh,2.8rem);aspect-ratio:1;display:grid;place-items:center;border-radius:50%;background:var(--colour);color:#111;font-weight:800}.identity{min-width:0;display:grid;text-align:left}.identity b,.identity small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.identity b{font-size:clamp(.72rem,1.8vh,1rem)}.identity small{font-size:clamp(.58rem,1.35vh,.76rem);opacity:.72}.cooldown{grid-column:1/-1;height:.25rem;border-radius:9px;background:#ffffff25;overflow:hidden}.cooldown i{display:block;width:100%;height:100%;transform-origin:left;background:var(--colour)}.connection{position:absolute;top:.35rem;right:.35rem;width:.55rem;aspect-ratio:1;border-radius:50%}.connection.online{background:#40dc74}.connection.away{background:#ffad32}.connection.offline{background:#ff4d5e}.arena{position:relative;height:71vh;overflow:hidden;border-radius:22px;background:linear-gradient(#6ac6f4,#bcecff 73%,#6db252 74%);box-shadow:inset 0 0 3rem #1d426055}.distance{position:absolute;z-index:4;top:1vh;left:1vw;padding:.35rem .65rem;border-radius:10px;background:#07152699;color:#fff;font-size:clamp(.7rem,1.6vh,.95rem)}.flyer{position:absolute;z-index:5;width:clamp(2.5rem,7vh,4.6rem);aspect-ratio:1;border-radius:50%;display:grid;place-items:center;background:#ffd65a;border:4px solid #fff2a6;color:#19160a;box-shadow:0 .5rem 1rem #17364d77;transition:transform .06s linear}.flyer i{position:absolute;left:-35%;width:55%;height:35%;border-radius:80% 20%;background:#fff}.flyer b{font-size:clamp(1.1rem,3.3vh,2rem)}.obstacle{position:absolute;inset-block:0}.obstacle i{position:absolute;left:0;width:100%;display:block;background:linear-gradient(90deg,#178a45,#45d66f,#15813e);border:3px solid #0a6230;box-sizing:border-box}.obstacle .upper{top:0;border-radius:0 0 12px 12px}.obstacle .lower{bottom:0;border-radius:12px 12px 0 0}.ground{position:absolute;bottom:0;left:0;right:0;height:7%;background:repeating-linear-gradient(90deg,#3d7d32 0 30px,#4b9139 30px 60px)}.overlay{position:fixed;z-index:40;inset:0;display:grid;place-items:center;background:#07111bd9}.overlay-card{width:min(70vw,620px);box-sizing:border-box;padding:4vh 4vw;border:3px solid #ffd65a;border-radius:28px;background:#14243a;color:#fff;text-align:center}.overlay-card h2{font-size:clamp(1.7rem,5vh,3.3rem);margin:0 0 1vh}.overlay-card p{font-size:clamp(.9rem,2.2vh,1.3rem)}.overlay-card>strong{display:block;font-size:clamp(1.4rem,4vh,2.7rem);color:#ffd65a}.overlay-card>small{display:block;margin-top:1vh;font-size:clamp(1rem,2.5vh,1.5rem)}.countdown strong{font-size:clamp(5rem,18vh,11rem)}.rescue-meter{height:2.2vh;margin:2vh 0;border-radius:99px;background:#ffffff20;overflow:hidden}.rescue-meter i{display:block;height:100%;background:#42dc7c;transition:width .12s}.result p{margin-bottom:0}@media(max-width:800px){.keep-page{padding:1vh 1.4vw}.players{height:17vh}.arena{height:69vh}.player{border-width:1px}.overlay-card{width:78vw}}
.flight-guide{position:absolute;z-index:4;top:1vh;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;padding:.35rem .8rem;border-radius:10px;background:#071526bb;color:#fff;text-align:center;font-size:clamp(.62rem,1.45vh,.85rem)}.flight-guide span{opacity:.78}
</style>
